import { m } from "@plannello/i18n/messages";
import type { ItemDetail, ItemLite } from "@plannello/shared";
import { useCatalog } from "@/data/CatalogProvider";
import type { ContentLanguage } from "./api";

/** The name of a language in the interface language ("English", "Englisch"). */
export function languageName(lang: ContentLanguage): string {
  return lang === "de" ? m.lang_name_de() : m.lang_name_en();
}

/** A language written in itself, the way language pickers show it. */
export function nativeName(lang: ContentLanguage): string {
  return lang === "de" ? "Deutsch" : "English";
}

/**
 * The language an item is written in: its own (detected or chosen) language, else the product's.
 * Derived from cached rows so it follows live edits and product changes at once; the detail's
 * `effectiveContentLanguage` only fills in before the catalog knows the product.
 */
export function useEffectiveContentLanguage(
  item: Pick<ItemLite, "productId" | "contentLanguage"> | undefined,
  detail?: Pick<ItemDetail, "effectiveContentLanguage"> | null,
): ContentLanguage | undefined {
  const catalog = useCatalog();
  if (!item) return undefined;
  return (
    item.contentLanguage ?? catalog.productById.get(item.productId)?.contentLanguage ?? detail?.effectiveContentLanguage
  );
}

/** The product's content language (spellcheck of new items and comments). */
export function useProductLanguage(productId: string | undefined): ContentLanguage | undefined {
  const catalog = useCatalog();
  return productId ? catalog.productById.get(productId)?.contentLanguage : undefined;
}
